import sanitizeHtml from 'sanitize-html';

export type MarketoFieldLocalization = {
	label?: string;
	placeholder?: string;
	ariaLabel?: string;
	instructionHtml?: string;
	html?: string;
};

export type MarketoFormLocalization = {
	locale: string;
	submitButton: string;
	fields: Record<string, MarketoFieldLocalization>;
	options: Record<string, string>;
	validationMessages: Record<string, string>;
};

type MarketoControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

type DisplayNamesConstructor = new (
	locales: string[],
	options: { type: 'region' },
) => {
	of: (code: string) => string | undefined;
};

const warnedLocalizationKeys = new Set<string>();

const sanitizeRichText = (html: string) =>
	sanitizeHtml(html, {
		allowedTags: ['a', 'br', 'em', 'p', 'span', 'strong'],
		allowedAttributes: {
			a: ['href', 'rel', 'target'],
		},
	});

export const normalizeMarketoKey = (value?: string | null): string =>
	(value ?? '')
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]/g, '');

const normalizeValidationMessage = (value?: string | null): string =>
	(value ?? '').replace(/\s+/g, ' ').trim().toLowerCase();

const warnInDevelopment = (key: string, message: string) => {
	if (process.env['NODE_ENV'] !== 'development' || warnedLocalizationKeys.has(key)) {
		return;
	}

	warnedLocalizationKeys.add(key);
	console.warn(message);
};

const getControlKey = (control?: MarketoControl | null): string =>
	normalizeMarketoKey(control?.name || control?.id);

const findControlById = (formElement: HTMLElement, id?: string | null): MarketoControl | null => {
	if (!id) {
		return null;
	}

	return (
		Array.from(formElement.querySelectorAll<MarketoControl>('input, select, textarea')).find(
			(control) => control.id === id,
		) ?? null
	);
};

const findAssociatedControl = (
	formElement: HTMLElement,
	element: Element,
): MarketoControl | null => {
	if (element instanceof HTMLLabelElement && element.htmlFor) {
		return findControlById(formElement, element.htmlFor);
	}

	const fieldWrap = element.closest('.mktoFieldWrap');
	return fieldWrap?.querySelector<MarketoControl>('input, select, textarea') ?? null;
};

const findOptionControl = (
	formElement: HTMLElement,
	label: HTMLLabelElement,
): HTMLInputElement | null => {
	if (label.htmlFor) {
		const control = findControlById(formElement, label.htmlFor);
		if (control instanceof HTMLInputElement) {
			return control;
		}
	}

	const previousElement = label.previousElementSibling;
	if (previousElement instanceof HTMLInputElement) {
		return previousElement;
	}

	return null;
};

const getFieldLocalization = (
	localization: MarketoFormLocalization,
	control?: MarketoControl | null,
): MarketoFieldLocalization | undefined => {
	const fieldKey = getControlKey(control);

	if (!fieldKey) {
		return undefined;
	}

	const fieldLocalization = localization.fields[fieldKey];
	if (!fieldLocalization) {
		warnInDevelopment(
			`field:${fieldKey}`,
			`Marketo form field localization missing for key "${fieldKey}".`,
		);
	}

	return fieldLocalization;
};

const getElementFieldLocalization = (
	formElement: HTMLElement,
	element: Element,
	localization: MarketoFormLocalization,
): MarketoFieldLocalization | undefined => {
	const associatedControl = findAssociatedControl(formElement, element);
	if (associatedControl) {
		return getFieldLocalization(localization, associatedControl);
	}

	const row = element.closest('.mktoFormRow');
	const rowFieldKey = Array.from(row?.classList ?? [])
		.map(normalizeMarketoKey)
		.find((key) => localization.fields[key]);

	return rowFieldKey ? localization.fields[rowFieldKey] : undefined;
};

const setLabelText = (label: HTMLLabelElement, text: string) => {
	const requiredAsterisks = Array.from(label.querySelectorAll('.mktoAsterix'));
	requiredAsterisks.forEach((asterisk) => asterisk.remove());
	label.textContent = text;
	requiredAsterisks.forEach((asterisk) => label.appendChild(asterisk));
};

const localizeFieldLabels = (formElement: HTMLElement, localization: MarketoFormLocalization) => {
	formElement.querySelectorAll<HTMLLabelElement>('label.mktoLabel').forEach((label) => {
		const control = findAssociatedControl(formElement, label);
		const fieldLocalization = getFieldLocalization(localization, control);

		if (fieldLocalization?.label) {
			setLabelText(label, fieldLocalization.label);
		}
	});
};

const localizeFieldAttributes = (
	formElement: HTMLElement,
	localization: MarketoFormLocalization,
) => {
	formElement.querySelectorAll<MarketoControl>('input, select, textarea').forEach((control) => {
		const fieldLocalization = getFieldLocalization(localization, control);

		if (!fieldLocalization) {
			return;
		}

		if (
			fieldLocalization.placeholder &&
			(control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement)
		) {
			control.placeholder = fieldLocalization.placeholder;
		}

		if (fieldLocalization.ariaLabel && (control.hasAttribute('aria-label') || !control.id)) {
			control.setAttribute('aria-label', fieldLocalization.ariaLabel);
		}
	});
};

const getOptionLocalization = (
	localization: MarketoFormLocalization,
	fieldKey: string,
	value: string,
	includeSharedOption = true,
): string | undefined => {
	const optionKey = normalizeMarketoKey(value);

	return (
		localization.options[`${fieldKey}:${optionKey}`] ??
		(includeSharedOption ? localization.options[optionKey] : undefined)
	);
};

const getLocalizedRegionName = (
	localization: MarketoFormLocalization,
	value: string,
): string | undefined => {
	if (!/^[A-Z]{2}$/i.test(value)) {
		return undefined;
	}

	const DisplayNames = (Intl as typeof Intl & { DisplayNames?: DisplayNamesConstructor })
		.DisplayNames;

	try {
		return DisplayNames
			? new DisplayNames([localization.locale], { type: 'region' }).of(value)
			: undefined;
	} catch {
		return undefined;
	}
};

const localizeSelectOptions = (formElement: HTMLElement, localization: MarketoFormLocalization) => {
	formElement.querySelectorAll<HTMLSelectElement>('select').forEach((select) => {
		const fieldKey = getControlKey(select);

		if (!fieldKey) {
			return;
		}

		select.querySelectorAll<HTMLOptionElement>('option').forEach((option) => {
			const localizedOption =
				getOptionLocalization(localization, fieldKey, option.value, fieldKey !== 'country') ??
				(fieldKey === 'country' ? getLocalizedRegionName(localization, option.value) : undefined);

			if (localizedOption) {
				option.textContent = localizedOption;
				return;
			}

			if (option.value || option.textContent?.trim()) {
				warnInDevelopment(
					`option:${fieldKey}:${option.value || option.textContent}`,
					`Marketo form option localization missing for key "${fieldKey}:${option.value}".`,
				);
			}
		});
	});
};

const localizeChoiceLabels = (formElement: HTMLElement, localization: MarketoFormLocalization) => {
	formElement
		.querySelectorAll<HTMLLabelElement>(
			'.mktoCheckboxList label:not(.mktoLabel), .mktoRadioList label:not(.mktoLabel)',
		)
		.forEach((label) => {
			const control = findOptionControl(formElement, label);
			const fieldKey = getControlKey(control);

			if (!control || !fieldKey) {
				return;
			}

			const checkboxList = label.closest('.mktoCheckboxList');
			const isOnlyCheckboxOption =
				control.type === 'checkbox' &&
				checkboxList?.querySelectorAll('input[type="checkbox"]').length === 1;
			const fieldLocalization = localization.fields[fieldKey];

			if (isOnlyCheckboxOption && fieldLocalization?.label) {
				label.textContent = fieldLocalization.label;
				return;
			}

			const localizedOption = getOptionLocalization(localization, fieldKey, control.value);
			if (localizedOption) {
				label.textContent = localizedOption;
				return;
			}

			warnInDevelopment(
				`option:${fieldKey}:${control.value}`,
				`Marketo form choice localization missing for key "${fieldKey}:${control.value}".`,
			);
		});
};

const isStandalonePrivacyNotice = (element: HTMLElement): boolean => {
	// Marketo's standalone HTML blocks have generated names rather than field aliases.
	const text = (element.textContent ?? '').replace(/\s+/g, ' ').trim();
	return (
		text === "By submitting this form, I acknowledge receipt of Atlassian's Privacy Policy." &&
		Boolean(element.querySelector('a[href="https://www.atlassian.com/legal/privacy-policy"]'))
	);
};

const localizeInstructions = (formElement: HTMLElement, localization: MarketoFormLocalization) => {
	formElement.querySelectorAll<HTMLElement>('.mktoInstruction').forEach((element) => {
		const fieldLocalization = getElementFieldLocalization(formElement, element, localization);

		if (fieldLocalization?.instructionHtml) {
			element.innerHTML = sanitizeRichText(fieldLocalization.instructionHtml);
		}
	});

	formElement.querySelectorAll<HTMLElement>('.mktoHtmlText').forEach((element) => {
		const fieldLocalization =
			getElementFieldLocalization(formElement, element, localization) ??
			(isStandalonePrivacyNotice(element) ? localization.fields['privacypolicy'] : undefined);

		if (fieldLocalization?.html) {
			element.innerHTML = sanitizeRichText(fieldLocalization.html);
		}
	});
};

const localizeSubmitButton = (formElement: HTMLElement, localization: MarketoFormLocalization) => {
	if (!localization.submitButton) {
		return;
	}

	formElement
		.querySelectorAll<HTMLButtonElement>('button[type="submit"], .mktoButton')
		.forEach((button) => {
			button.textContent = localization.submitButton;
		});
};

export const localizeMarketoValidationMessages = (
	formElement: HTMLElement,
	localization: MarketoFormLocalization,
) => {
	formElement.querySelectorAll<HTMLElement>('.mktoErrorMsg').forEach((element) => {
		const validationKey = normalizeValidationMessage(element.textContent);
		const localizedMessage = localization.validationMessages[validationKey];

		if (localizedMessage && element.textContent !== localizedMessage) {
			element.textContent = localizedMessage;
			return;
		}

		if (validationKey && !localizedMessage) {
			warnInDevelopment(
				`validation:${validationKey}`,
				`Marketo form validation localization missing for message "${validationKey}".`,
			);
		}
	});
};

export const applyMarketoFormLocalization = (
	formElement: HTMLElement,
	localization: MarketoFormLocalization,
) => {
	localizeFieldLabels(formElement, localization);
	localizeFieldAttributes(formElement, localization);
	localizeSelectOptions(formElement, localization);
	localizeChoiceLabels(formElement, localization);
	localizeInstructions(formElement, localization);
	localizeSubmitButton(formElement, localization);
	localizeMarketoValidationMessages(formElement, localization);
};

export const observeMarketoValidationLocalization = (
	formElement: HTMLElement,
	localization: MarketoFormLocalization,
): MutationObserver | undefined => {
	if (typeof MutationObserver === 'undefined') {
		return undefined;
	}

	const observer = new MutationObserver(() => {
		localizeMarketoValidationMessages(formElement, localization);
	});
	observer.observe(formElement, {
		childList: true,
		subtree: true,
		characterData: true,
	});

	return observer;
};
